import React from "react";
import { isMobile } from "@kubera/common";
import requestIdleCallback from "utilities/requestIdleCallback";
import { routes } from "routes";

const dashboardRouteChunks = {
  [routes.REPORTS]: () => import("components/recap/RecapComponent"),
  [routes.PLANNING]: () => import("components/planning/PlanningComponent"),
  [routes.OWNERSHIP]: () => import("components/ownership/OwnershipComponent"),
  [routes.PLUGIN]: () => import("components/plugin/PluginWrapper"),
  [routes.NET_WORTH]: () => import("components/net_worth/NetWorthComponent"),
  [routes.INVESTABLE]: () => import("components/net_worth/MobileInvestableComponent")
};

const mobileRouteChunkOrder = [routes.NET_WORTH, routes.INVESTABLE];
const desktopRouteChunkOrder = [routes.NET_WORTH, routes.REPORTS, routes.PLANNING, routes.OWNERSHIP, routes.PLUGIN];

const preloadedRouteModules = {};
const lazilyRenderedPaths = {};

const loadRouteChunk = path => {
  const loadChunk = dashboardRouteChunks[path];
  if (!loadChunk) {
    return Promise.resolve();
  }
  return loadChunk()
    .then(routeModule => {
      preloadedRouteModules[path] = routeModule;
    })
    .catch(() => {});
};

const landingRouteFor = path => {
  if (path !== routes.HOME) {
    return path;
  }
  return isMobile() === true ? null : routes.NET_WORTH;
};

export const loadDashboardRouteForCurrentPath = () => loadRouteChunk(landingRouteFor(window.location.pathname));

let hasQueuedRouteChunkPrefetch = false;

export const prefetchDashboardRouteChunks = () => {
  if (hasQueuedRouteChunkPrefetch === true) {
    return;
  }
  hasQueuedRouteChunkPrefetch = true;

  const pendingPaths = (isMobile() === true ? mobileRouteChunkOrder : desktopRouteChunkOrder).filter(
    path => !preloadedRouteModules[path] && !lazilyRenderedPaths[path]
  );

  const loadNextChunk = () => {
    const path = pendingPaths.shift();
    if (!path) {
      return;
    }
    requestIdleCallback(() => loadRouteChunk(path).then(loadNextChunk));
  };
  loadNextChunk();
};

export const lazyDashboardRoute = path =>
  React.lazy(() => {
    lazilyRenderedPaths[path] = true;
    return dashboardRouteChunks[path]();
  });

// React.lazy suspends on its first render even when the chunk is already in the module
// registry, because the import() it calls settles a microtask later — long enough for the
// fallback to commit. Handing back the resolved component instead is what actually keeps
// the landing route from flashing an empty content area. Once a route has rendered through
// its lazy wrapper it must keep doing so: swapping to the module's component changes the
// element type and React would remount the route.
export const routeComponentFor = (path, lazyComponent) =>
  lazilyRenderedPaths[path] ? lazyComponent : preloadedRouteModules[path]?.default ?? lazyComponent;
